import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCourseContent } from '../hooks/useContentData';
import { GrammatikSection } from '../components/GrammatikSection';
import { PflegeQuiz, QUIZ_ANSWERS } from '../components/PflegeQuiz';
import { ExerciseSection } from '../components/ExerciseSection';
import { PracticeButton } from '../components/pflege/PflegePracticeButton';
import { TabBegruessung, TabKoerper, TabAlltag, TabZahlen } from '../components/pflege/PflegeTabsBasic';
import { TabGrammatik, TabDialoge, TabWortschatz, TabNotfall } from '../components/pflege/PflegeTabsAdvanced';
import EXERCISES_PFLEGE from '../data/exerciseBankPflege';
import '../styles/pflege-training.css';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PflegeTraining() {
  const [activeTab, setActiveTab] = useState('b1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);
  const [previousBest, setPreviousBest] = useState(null);
  const { lessons: pflegeLessons, grammatik: grammatikPflege, loading } = useCourseContent('pflege');

  useEffect(() => {
    const fetchQuizResults = async () => {
      try {
        const res = await axios.get(`${API}/quiz/results`);
        const pflege = (res.data.results || []).find(r => r.quiz_id === 'pflege_quiz');
        if (pflege) setPreviousBest(pflege);
      } catch (err) {
        toast.error('Could not load quiz results');
      }
    };
    fetchQuizResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    { id: 'b1', label: 'Begrüßung', icon: '🤝' },
    { id: 'b2', label: 'Körper', icon: '🫁' },
    { id: 'b3', label: 'Alltag', icon: '🛏️' },
    { id: 'b4', label: 'Zahlen', icon: '🔢' },
    { id: 'pg1', label: 'Grammatik 1-4', icon: '📐', isGrammatik: true },
    { id: 'b5', label: 'Grammatik', icon: '📝' },
    { id: 'b6', label: 'Dialoge', icon: '💬' },
    { id: 'b7', label: 'Wortschatz', icon: '📚' },
    { id: 'b8', label: 'Notfall', icon: '🚨' },
    { id: 'pg2', label: 'Grammatik 5-8', icon: '📐', isGrammatik: true },
    { id: 'b10', label: 'Verben', icon: '🔄' },
    { id: 'b11', label: 'Adjektive', icon: '🌡️' },
    { id: 'b12', label: 'Familie', icon: '👨‍👩‍👧‍👦' },
    { id: 'b13', label: 'Dokumentation', icon: '📋' },
    { id: 'pg3', label: 'Grammatik 9-12', icon: '📐', isGrammatik: true },
    { id: 'b14', label: 'Schichten', icon: '🕐' },
    { id: 'b15', label: 'Vitalzeichen', icon: '💓' },
    { id: 'b16', label: 'Mobilisation', icon: '🦽' },
    { id: 'pg4', label: 'Grammatik 13-16', icon: '📐', isGrammatik: true },
    { id: 'b17', label: 'Übungen', icon: '🏋️', isExercise: true },
    { id: 'b9', label: 'Quiz', icon: '✅', isQuiz: true },
  ];

  const submitQuiz = async () => {
    setShowResults(true);
    const score = calculateScore();
    setQuizSubmitting(true);
    try {
      const res = await axios.post(`${API}/quiz/submit`, {
        quiz_id: 'pflege_quiz', course_id: 'pflege',
        correct: score.correct, total: score.total, percentage: score.percentage
      });
      setXpEarned(res.data.xp_earned || 0);
      setPreviousBest(prev => {
        if (!prev || score.percentage > (prev.best_percentage || 0)) {
          return { ...prev, best_percentage: score.percentage, best_correct: score.correct, percentage: score.percentage };
        }
        return prev;
      });
      if (res.data.is_new_best) {
        toast.success(`New best score! +${res.data.xp_earned} XP`);
      } else {
        toast.info(`+${res.data.xp_earned} XP earned`);
      }
    } catch (err) {
      toast.error('Could not save quiz result');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const calculateScore = () => {
    let correct = 0;
    const total = Object.keys(QUIZ_ANSWERS).length;
    Object.keys(QUIZ_ANSWERS).forEach(q => {
      if (quizAnswers[q] === QUIZ_ANSWERS[q]) correct++;
    });
    return { correct, total, percentage: Math.round((correct / total) * 100) };
  };

  const resetQuiz = () => { setQuizAnswers({}); setShowResults(false); setXpEarned(0); };

  /* Generic renderer for backend-driven module tabs (b10-b16) */
  const renderModuleTab = (tabId, moduleData) => (
    <div className={`pflege-section ${activeTab === tabId ? 'active' : ''}`} data-testid={`tab-${tabId}`}>
      <h2 style={{fontSize: '22px', fontWeight: '700', marginBottom: '8px'}}>{moduleData.title.de}</h2>
      <p style={{color: '#94a3b8', marginBottom: '20px'}}>{moduleData.title.en} · <span className="ar">{moduleData.title.ar}</span></p>
      {moduleData.sections.map((section, si) => {
        const sKey = `${tabId}-${section.type}-${si}`;
        if (section.type === 'vocab_table') {
          return (
            <div key={sKey} className="pflege-card">
              <div className="pflege-card-header">
                <span className="pflege-card-icon">{section.icon}</span>
                <h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3>
              </div>
              <div className="pflege-card-body">
                <table className="vocab-table">
                  <thead><tr><th>Deutsch</th><th>English</th><th className="ar">عربي</th>{section.hasAudio && <th>Üben</th>}</tr></thead>
                  <tbody>
                    {section.items.map((item, i) => (
                      <tr key={`row-${item.de?.slice(0,8)}-${i}`}>
                        <td className="de">{item.de}</td><td className="en">{item.en}</td><td className="ar">{item.ar}</td>
                        {section.hasAudio && <td><PracticeButton text={item.de} /></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        }
        if (section.type === 'grammar_table') {
          return (
            <div key={sKey} className="pflege-card">
              <div className="pflege-card-header">
                <span className="pflege-card-icon">{section.icon}</span>
                <h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3>
              </div>
              <div className="pflege-card-body">
                {section.subtitle?.de && <p style={{color: '#94a3b8', fontSize: '13px', marginBottom: '12px'}}>{section.subtitle.de}</p>}
                <div className="grammar-box">
                  <table className="grammar-table">
                    <thead><tr>{section.headers.map((h, hi) => <th key={`h-${h}-${hi}`} className={h === 'عربي' ? 'ar' : ''}>{h}</th>)}</tr></thead>
                    <tbody>
                      {section.rows.map((row, ri) => (
                        <tr key={`row-${ri}-${row[0]}`}>
                          {row.map((cell, ci) => (
                            <td key={`c-${ri}-${ci}`} className={ci === 1 ? 'em' : ci === row.length - 1 ? 'ar' : ''}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        }
        if (section.type === 'body_grid') {
          return (
            <div key={sKey} className="pflege-card">
              <div className="pflege-card-header">
                <span className="pflege-card-icon">{section.icon}</span>
                <h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3>
              </div>
              <div className="pflege-card-body">
                <div className="body-grid">
                  {section.items.map((item, i) => (
                    <div key={`item-${item.de?.slice(0,8)}-${i}`} className="body-item">
                      <div className="de">{item.de}</div><div className="en">{item.en}</div><div className="ar">{item.ar}</div>
                      <div className="mt-1"><PracticeButton text={item.de} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }
        if (section.type === 'phrase_grid') {
          return (
            <div key={sKey} className="pflege-card">
              <div className="pflege-card-header">
                <span className="pflege-card-icon">{section.icon}</span>
                <h3>{section.title.de} · {section.title.en} · <span className="ar">{section.title.ar}</span></h3>
              </div>
              <div className="pflege-card-body">
                <div className="phrase-grid">
                  {section.items.map((item, i) => (
                    <div key={`p-${item.ctx}-${i}`} className="phrase-card">
                      <div className="phrase-ctx">{item.ctx}</div>
                      <div className="phrase-de flex items-center justify-between">
                        <span>{item.de}</span><PracticeButton text={item.de} />
                      </div>
                      <div className="phrase-en">{item.en}</div><div className="phrase-ar">{item.ar}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }
        if (section.type === 'dialog') {
          return (
            <div key={sKey} className="dialog-box">
              <div className="dialog-title">{section.title}</div>
              <div className="dialog-content">
                {section.lines.map((line, j) => (
                  <div key={`dl-${line.speaker}-${j}`} className="dialog-line">
                    <div className={`dialog-speaker ${line.speaker.toLowerCase()}`}>{line.speaker}:</div>
                    <div className={`dialog-bubble ${line.speaker === 'Patient' ? 'patient' : ''}`}>
                      <div className="de" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span>{line.de}</span><PracticeButton text={line.de} />
                      </div>
                      <div className="en">{line.en}</div><div className="ar">{line.ar}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (section.type === 'tip') {
          return (
            <div key={sKey} className="tip-box" style={{marginTop: '16px'}}>
              <strong>Tipp · Tip · <span className="ar">نصيحة</span></strong><br/>
              {section.text.de} · {section.text.en} · <span className="ar">{section.text.ar}</span>
            </div>
          );
        }
        return null;
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="pflege-training-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="pflege-back-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            <span className="text-sm">Back</span>
          </Link>
          <h1 className="text-teal-500 font-bold">EGNS Pflege Training</h1>
          <div className="w-20"></div>
        </div>
      </div>

      <div className="pflege-container">
        <div className="pflege-header">
          <h1>Deutsch für Pflegekräfte – A1.1</h1>
          <p>German for Nurses · <span className="ar" style={{color: '#14b8a6'}}>الألمانية للممرضين</span></p>
        </div>

        {/* Tab Navigation */}
        <div className="tab-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${tab.isGrammatik ? 'grammatik-tab' : ''} ${tab.isExercise ? 'exercise-tab' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-btn-${tab.id}`}
              style={tab.isQuiz && previousBest ? {position: 'relative'} : {}}
            >
              {tab.icon} {tab.label}
              {tab.isQuiz && previousBest && (
                <span style={{ position: 'absolute', top: '-4px', right: '-4px', width: '14px', height: '14px', borderRadius: '50%', background: '#14b8a6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckCircle2 style={{width: '10px', height: '10px', color: 'white'}} />
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Static Tabs (b1-b8) */}
        <TabBegruessung isActive={activeTab === 'b1'} />
        <TabKoerper isActive={activeTab === 'b2'} />
        <TabAlltag isActive={activeTab === 'b3'} />
        <TabZahlen isActive={activeTab === 'b4'} />
        <TabGrammatik isActive={activeTab === 'b5'} />
        <TabDialoge isActive={activeTab === 'b6'} />
        <TabWortschatz isActive={activeTab === 'b7'} />
        <TabNotfall isActive={activeTab === 'b8'} />

        {/* Backend-driven Tabs (b10-b16) */}
        {!loading && pflegeLessons['verbenData'] && renderModuleTab('b10', pflegeLessons['verbenData'])}
        {!loading && pflegeLessons['adjektiveData'] && renderModuleTab('b11', pflegeLessons['adjektiveData'])}
        {!loading && pflegeLessons['familieData'] && renderModuleTab('b12', pflegeLessons['familieData'])}
        {!loading && pflegeLessons['dokumentationData'] && renderModuleTab('b13', pflegeLessons['dokumentationData'])}
        {!loading && pflegeLessons['schichtenData'] && renderModuleTab('b14', pflegeLessons['schichtenData'])}
        {!loading && pflegeLessons['vitalzeichenData'] && renderModuleTab('b15', pflegeLessons['vitalzeichenData'])}
        {!loading && pflegeLessons['mobilisationData'] && renderModuleTab('b16', pflegeLessons['mobilisationData'])}

        {/* Pflege Grammatik Tabs */}
        {!loading && ['pg1', 'pg2', 'pg3', 'pg4'].map(gId => {
          const gramMap = { pg1: ['m1','m2','m3','m4'], pg2: ['m5','m6','m7','m8'], pg3: ['m9','m10','m11','m12'], pg4: ['m13','m14','m15','m16'] };
          return (
            <div key={gId} className={`pflege-section ${activeTab === gId ? 'active' : ''}`} data-testid={`tab-${gId}`}>
              {gramMap[gId].map(mid => (
                grammatikPflege[mid] && <GrammatikSection key={mid} data={grammatikPflege[mid]} isActive={activeTab === gId} />
              ))}
            </div>
          );
        })}

        {/* Übungen Tab */}
        <div className={`pflege-section ${activeTab === 'b17' ? 'active' : ''}`} data-testid="tab-b17">
          <PflegeExercisePicker />
        </div>

        {/* Quiz Tab */}
        <div className={`pflege-section ${activeTab === 'b9' ? 'active' : ''}`}>
          <PflegeQuiz
            quizAnswers={quizAnswers} showResults={showResults}
            onAnswer={(qId, idx) => setQuizAnswers(prev => ({ ...prev, [qId]: idx }))}
            onSubmit={submitQuiz} onReset={resetQuiz}
            submitting={quizSubmitting} previousBest={previousBest} xpEarned={xpEarned}
          />
        </div>
      </div>
    </div>
  );
}

function PflegeExercisePicker() {
  const [selectedLesson, setSelectedLesson] = useState('B1');
  const keys = ['B1','B2','B3','B4','B5','B6','B7','B8','B9','B10','B11','B12'];
  const labels = ['B1: Begrüßung','B2: Körper','B3: Alltag','B4: Zahlen','B5: Grammatik','B6: Dialoge','B7: Wortschatz','B8: Notfall','B9: Materialien','B10: Anatomie','B11: Übergabe','B12: Aufnahme'];
  return (
    <div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px' }}>Übungen / Exercises</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        {keys.map((key, i) => (
          <button key={key} onClick={() => setSelectedLesson(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedLesson === key ? 'bg-teal-600 text-white' : 'bg-muted/50 text-muted-foreground hover:bg-muted border border-border'}`}
            data-testid={`exercise-lesson-${key}`}>{labels[i]}</button>
        ))}
      </div>
      <ExerciseSection lessonId={selectedLesson} isActive={true} exercises={EXERCISES_PFLEGE} />
    </div>
  );
}
